'use client';

/**
 * Uploads vanuit de browser.
 *
 * Achtergrond: /api/upload is een Vercel Serverless Function en het platform
 * weigert elke aanvraag met een body boven ~4,5 MB met een kale 413 voordat de
 * route ook maar draait. Een ontwerp uit de designer is al snel groter, dus
 * elke klant met een echte foto kreeg "Opslaan mislukt". De oplossing is niet
 * een hogere limiet in de route (die wordt nooit bereikt) maar het bestand
 * langs de functie heen sturen: de browser krijgt een kortlopend, beperkt
 * token en zet het bestand rechtstreeks in Vercel Blob. Daarna registreren we
 * alleen nog de URL plus wat metadata, een paar honderd bytes.
 *
 * BLOB_READ_WRITE_TOKEN komt hierbij nooit in de browser terecht:
 * /api/upload/blob-token tekent een token dat alleen dit ene pad, dit ene
 * type en deze maximale grootte toestaat.
 */

import { upload } from '@vercel/blob/client';
import { getEditorSessionUploadCapability } from '@/lib/editor-session/client';
import { reportOrderPathFailure, reportOrderPathWarning, type OrderPathStep } from '@/lib/monitoring/report-client-error';
import {
  DIRECT_UPLOAD_THRESHOLD_BYTES,
  MULTIPART_UPLOAD_THRESHOLD_BYTES,
  UPLOAD_STALL_ABORT_MS,
  isAllowedUploadContentType,
  normalizeContentType,
} from './limits';
import {
  ARTWORK_HEAD_BYTES,
  ArtworkFormatError,
  MAX_SVG_UPLOAD_BYTES,
  POSTSCRIPT_MIME,
  SVG_MIME,
  canonicalUploadType,
  isVectorArtworkFileName,
  svgSafetyIssue,
  type ArtworkDetection,
} from './artwork-formats';

/**
 * De fasen die een klant achter elkaar ziet. Ze zijn los benoemd omdat een
 * balk die op 100% blijft staan terwijl er nog iets gebeurt als vastgelopen
 * overkomt: na `uploading` volgt nog `finishing` (de registratie).
 */
export type UploadPhase = 'preparing' | 'uploading' | 'finishing' | 'done' | 'error';

export interface UploadProgressDetail {
  phase: UploadPhase;
  loaded: number;
  total: number;
  percentage: number;
}

/** Precies de velden die alle aanroepers van /api/upload gebruiken. */
export interface RegisteredUpload {
  /** Opt-in capability for a subsequent owner-verified processing request. Never log. */
  proof?: string;
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  providerFileId?: string;
}

export interface UploadToStorageOptions {
  /** Used by image processing to obtain a signed, exact-file capability. */
  requireProof?: boolean;
  productId?: string;
  providerType?: string;
  /**
   * Database-id van de actieve editor-sessie (getCurrentEditorSessionId in
   * editor-session/client.ts), zodat /admin/editor-sessions/[code] deze
   * upload aan de sessie kan tonen. Ontbreekt hij (nog geen sessie, of de
   * aanroeper kent er geen), dan wordt de upload gewoon zonder koppeling
   * opgeslagen - nooit een reden om de upload zelf te laten mislukken.
   */
  editorSessionId?: string;
  /** Klantzichtbare tekst bij een mislukking, in de taal van de pagina. */
  failureMessage: string;
  /** Klantzichtbare tekst als een vectorbestand niet bij zijn extensie past. */
  invalidFileMessage?: string;
  /** Klantzichtbare tekst als de overdracht vastloopt en wordt afgebroken. */
  timeoutMessage?: string;
  /** Sentry-stap; 'design-upload' vanuit de designer, 'file-upload' bij een sleep-en-neerzet. */
  step?: OrderPathStep;
  onProgress?: (detail: UploadProgressDetail) => void;
}

/** A customer-facing 400 validation response, not an infrastructure failure. */
export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

/**
 * Voortgang wordt ook op `window` gepubliceerd, zodat componenten die niets
 * van deze module weten kunnen meeluisteren. Concreet: de opslaan-overlay van
 * de designer (`design-editor-modal.tsx`) kan hierop abonneren zonder dat er
 * een prop of context tussen hoeft. Zelfde patroon als `printen:pricing`, dat
 * de configurator naar de mobiele balk stuurt.
 *
 *   window.addEventListener('printen:upload-progress', (event) => {
 *     const { phase, loaded, total, percentage } = (event as CustomEvent).detail;
 *   });
 *
 * detail: { phase, loaded, total, percentage }
 */
export function publishUploadProgress(detail: UploadProgressDetail) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('printen:upload-progress', { detail }));
  } catch {
    // Voortgang melden mag een upload nooit laten stuklopen.
  }
}

async function readJsonResponse(response: Response): Promise<Record<string, any>> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    // Een 413 van Vercel komt terug als kale edge-id, geen JSON. Die tekst is
    // waardevol in de melding, dus hem bewaren in plaats van weggooien.
    return { error: text.slice(0, 180) };
  }
}

/**
 * Zet een data-URL om in een File, en doet dat precies één keer.
 *
 * `fetch(dataUrl)` laat de base64-decodering aan de browser over in plaats van
 * er in JavaScript een tweede kopie van te maken. Base64 is ~1,33x de bytes,
 * dus dit scheelt op een groot printbestand tientallen tot honderden MB's.
 * De aanroeper hoort na deze aanroep zijn eigen verwijzing naar de data-URL
 * los te laten, anders staan de string en de blob samen in het geheugen.
 */
export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('Ontwerp kon niet worden opgeslagen.');
  }
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  // Safari can preserve data-URL parameters in Blob.type (for example
  // `application/pdf;charset=utf-8`). The upload API intentionally accepts
  // only canonical MIME types, so normalize before creating the File instead
  // of letting valid editor PDFs be rejected as an unsupported file type.
  const dataUrlType = /^data:([^;,]+)/i.exec(dataUrl)?.[1] || '';
  let contentType = normalizeContentType(blob.type || dataUrlType);
  if (contentType === 'image/jpg') contentType = 'image/jpeg';
  if (!isAllowedUploadContentType(contentType)) {
    throw new Error('Het bestandstype van dit ontwerp wordt niet ondersteund.');
  }
  return new window.File([blob], fileName, { type: contentType });
}

/** De server weigerde de inhoud (EPS die geen EPS is, onveilige SVG): toon de tekst in de taal van de klant. */
function isContentRejection(code: unknown): boolean {
  return typeof code === 'string' && (code.startsWith('ARTWORK_') || code === 'UPLOAD_CONTENT_INVALID');
}

/**
 * Snelle eerste schifting voor bestandskiezers: mag dit bestand de upload in?
 * Vectorbestanden worden op extensie toegelaten omdat browsers er vaak geen
 * of een verkeerd type aan geven; de echte inhoudscontrole volgt in
 * `prepareArtworkFile` en op de server.
 */
export function isAcceptedArtworkFile(file: { name: string; type: string }): boolean {
  return isAllowedUploadContentType(file.type) && file.type !== POSTSCRIPT_MIME && file.type !== SVG_MIME
    || isVectorArtworkFileName(file.name);
}

/**
 * Geeft een vectorbestand het type en de naam die de server verwacht.
 *
 * Een .eps of .ai heeft in de browser vaak een leeg of afwijkend type, en de
 * directe Blob-route vergelijkt dat type letterlijk met het ondertekende
 * bewijs. Daarom hier één keer vaststellen uit naam en kopbytes. Een
 * PDF-compatibele .ai krijgt `.pdf` achter de naam en gaat als PDF verder.
 * Andere bestanden komen ongewijzigd terug.
 */
export async function prepareArtworkFile(file: File, invalidMessage: string): Promise<{ file: File; detection: ArtworkDetection | null }> {
  if (!isVectorArtworkFileName(file.name) && file.type !== POSTSCRIPT_MIME && file.type !== SVG_MIME) {
    return { file, detection: null };
  }
  try {
    const isSvg = /\.svg$/i.test(file.name);
    if (isSvg && file.size > MAX_SVG_UPLOAD_BYTES) throw new ArtworkFormatError('ARTWORK_TOO_LARGE');
    const head = new Uint8Array(await file.slice(0, isSvg ? file.size : ARTWORK_HEAD_BYTES).arrayBuffer());
    const { mimeType, fileName, detection } = canonicalUploadType(file.name, file.type, head);
    if (!detection) throw new ArtworkFormatError('ARTWORK_CONTENT_MISMATCH');
    if (isSvg && svgSafetyIssue(new TextDecoder().decode(head))) throw new ArtworkFormatError('ARTWORK_UNSAFE_SVG');
    if (mimeType === file.type && fileName === file.name) return { file, detection };
    return { file: new window.File([file], fileName, { type: mimeType, lastModified: file.lastModified }), detection };
  } catch (error) {
    if (error instanceof ArtworkFormatError) throw new UploadValidationError(invalidMessage);
    throw error;
  }
}

/**
 * Uploadt een bestand en levert exact dezelfde vorm op die /api/upload al
 * teruggaf, zodat de rest van de code niets merkt van welk pad is gebruikt.
 */
export async function uploadFileToStorage(
  file: File,
  options: UploadToStorageOptions
): Promise<RegisteredUpload> {
  const { productId, providerType, editorSessionId, failureMessage, timeoutMessage, onProgress } = options;
  file = (await prepareArtworkFile(file, options.invalidFileMessage || failureMessage)).file;
  const step: OrderPathStep = options.step ?? 'file-upload';
  const total = file.size;
  const editorSessionCapability = getEditorSessionUploadCapability(editorSessionId);

  const emit = (phase: UploadPhase, loaded: number) => {
    const percentage = total > 0 ? Math.min(100, Math.max(0, Math.round((loaded / total) * 100))) : 0;
    const detail: UploadProgressDetail = { phase, loaded, total, percentage };
    try {
      onProgress?.(detail);
    } catch {
      // idem
    }
    publishUploadProgress(detail);
  };

  // Kleine bestanden blijven over de bestaande route lopen: één rondgang, en
  // het is het pad dat al draait. Alleen boven de platformgrens wijken we uit.
  if (total <= DIRECT_UPLOAD_THRESHOLD_BYTES && !options.requireProof) {
    emit('uploading', 0);
    let response: Response;
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (productId) formData.append('productId', productId);
      if (providerType) formData.append('providerType', providerType);
      if (editorSessionId) formData.append('editorSessionId', editorSessionId);
      if (editorSessionCapability) formData.append('editorSessionCapability', editorSessionCapability);
      response = await fetch('/api/upload', { method: 'POST', body: formData });
    } catch (error) {
      reportOrderPathFailure(step, error, { bytes: total, product: productId, path: 'legacy-route' });
      emit('error', 0);
      throw new Error(failureMessage);
    }

    const data = await readJsonResponse(response);
    if (!response.ok || !data?.upload?.id) {
      if (response.status === 400) {
        // Validatiefouten waren eerder helemaal uit Sentry gefilterd, waardoor
        // de Safari-MIME-mismatch alleen bij de klant zichtbaar was.
        reportOrderPathWarning(step, 'upload rejected by validation', {
          status: response.status,
          code: typeof data?.code === 'string' ? data.code : undefined,
          bytes: total,
          product: productId,
          path: 'legacy-route',
        });
      }
      // 400 is bewuste validatie (verkeerd type, te groot). Dat is geen defect
      // en hoort niet als fout in Sentry. Al het andere wel: juist een 413 of
      // een 5xx op dit pad blokkeert een bestelling.
      if (response.status !== 400) {
        reportOrderPathFailure(step, new Error(`Upload via /api/upload mislukt (${response.status})`), {
          status: response.status,
          bytes: total,
          product: productId,
          path: 'legacy-route',
          responseSnippet: typeof data?.error === 'string' ? data.error.slice(0, 180) : undefined,
        });
      }
      emit('error', 0);
      if (response.status === 400) throw new UploadValidationError(isContentRejection(data?.code) && options.invalidFileMessage ? options.invalidFileMessage : data?.error || failureMessage);
      throw new Error(data?.error || failureMessage);
    }

    emit('uploading', total);
    emit('finishing', total);
    emit('done', total);
    return data.upload as RegisteredUpload;
  }

  // Grote bestanden: rechtstreeks naar Vercel Blob, langs de functiegrens heen.
  const multipart = total > MULTIPART_UPLOAD_THRESHOLD_BYTES;
  emit('preparing', 0);
  let intent: { pathname: string; proof: string };
  try {
    const response = await fetch('/api/upload/intent', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, fileSize: total, mimeType: file.type, editorSessionId, editorSessionCapability }),
    });
    const data = await readJsonResponse(response);
    if (!response.ok || typeof data.pathname !== 'string' || typeof data.proof !== 'string') {
      if (response.status === 400) {
        reportOrderPathWarning(step, 'upload intent rejected by validation', {
          status: response.status,
          code: typeof data?.code === 'string' ? data.code : undefined,
          bytes: total,
          product: productId,
          mimeType: file.type,
        });
      }
      throw new Error('Upload intent unavailable');
    }
    intent = { pathname: data.pathname, proof: data.proof };
  } catch {
    emit('error', 0);
    throw new Error(failureMessage);
  }

  // Bewaker tegen een overdracht die blijft hangen.
  //
  // De SDK probeert een mislukt blok tot tien keer opnieuw met verdubbelende
  // wachttijd (1, 2, 4 ... 512 seconden). Met een doorgeknipte verbinding
  // gemeten: na 205 seconden was de belofte nog steeds niet verworpen, want de
  // SDK lag tussen twee pogingen te wachten. `controller.abort()` alleen helpt
  // dan niet: dat grijpt pas bij de volgende poging, en die kan minuten later
  // zijn. Daarom naast het afbreken ook zelf opgeven, zodat de klant een echte
  // fout krijgt die hij kan herhalen in plaats van een balk die niet beweegt.
  const controller = new AbortController();
  let lastProgressAt = Date.now();
  let abortedByWatchdog = false;
  let watchdog: ReturnType<typeof setInterval> | undefined;
  const stallGuard = new Promise<never>((_resolve, reject) => {
    watchdog = setInterval(() => {
      if (Date.now() - lastProgressAt < UPLOAD_STALL_ABORT_MS) return;
      abortedByWatchdog = true;
      controller.abort();
      reject(new Error('Upload stalled: no progress for too long'));
    }, 5000);
  });

  let blob: { url: string; pathname: string; contentType: string };
  try {
    emit('uploading', 0);
    const uploadPromise = upload(intent.pathname, file, {
      access: 'public',
      handleUploadUrl: '/api/upload/blob-token',
      contentType: file.type || undefined,
      multipart,
      abortSignal: controller.signal,
      clientPayload: JSON.stringify({ productId, providerType, proof: intent.proof }),
      onUploadProgress: ({ loaded }) => {
        lastProgressAt = Date.now();
        emit('uploading', loaded);
      },
    });
    // Als de bewaker wint, komt de afwijzing van de SDK later alsnog binnen.
    // Zonder deze lege vanger is dat een onafgehandelde afwijzing in de console.
    uploadPromise.catch(() => {});
    blob = await Promise.race([uploadPromise, stallGuard]);
  } catch (error) {
    // Afgebroken verbinding of een halve multipart komt hier terecht. Er is dan
    // geen blobobject en dus ook geen databaseregel: geen half geschreven
    // toestand, de klant kan gewoon opnieuw opslaan.
    reportOrderPathFailure(step, error, {
      bytes: total,
      product: productId,
      path: 'blob-direct',
      multipart,
      abortedByWatchdog,
      stalledForMs: Date.now() - lastProgressAt,
    });
    emit('error', 0);
    throw new Error(abortedByWatchdog ? timeoutMessage || failureMessage : failureMessage);
  } finally {
    if (watchdog) clearInterval(watchdog);
  }

  // Vanaf hier bestaat het bestand wel maar kent de bestelling het nog niet.
  emit('finishing', total);

  const registrationBody = JSON.stringify({
    url: blob.url,
    proof: intent.proof,
    pathname: blob.pathname,
    fileName: file.name,
    fileSize: total,
    mimeType: file.type || blob.contentType,
    productId,
    providerType,
    editorSessionId,
  });
  let registerResponse: Response | undefined;
  let registrationError: unknown;
  // Registratie is idempotent per Blob-URL en bewijs. Herhaal één keer bij een
  // mobiele netwerkonderbreking of tijdelijke 5xx, zodat een bestand dat al
  // veilig in Blob staat niet onnodig als mislukte opslag eindigt.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      registerResponse = await fetch('/api/upload/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: registrationBody,
      });
      if (registerResponse.ok || registerResponse.status < 500 || attempt === 1) break;
    } catch (error) {
      registrationError = error;
      registerResponse = undefined;
      if (attempt === 1) break;
    }
  }
  if (!registerResponse) {
    // Het bestand staat er wel; dezelfde idempotente registratie kan later
    // opnieuw worden aangeboden. Neem de Blob-locatie mee voor herstel.
    reportOrderPathFailure(step, registrationError || new Error('Upload registration failed'), {
      bytes: total,
      product: productId,
      path: 'register',
      blobUrl: blob.url,
    });
    emit('error', 0);
    throw new Error(failureMessage);
  }

  const registerData = await readJsonResponse(registerResponse);
  if (!registerResponse.ok || !registerData?.upload?.id) {
    if (registerResponse.status === 400) {
      reportOrderPathWarning(step, 'upload registration rejected by validation', {
        status: registerResponse.status,
        code: typeof registerData?.code === 'string' ? registerData.code : undefined,
        bytes: total,
        product: productId,
        mimeType: file.type,
        path: 'register',
      });
    }
    if (registerResponse.status !== 400) {
      reportOrderPathFailure(
        step,
        new Error(`Registratie van de blob mislukt (${registerResponse.status})`),
        {
          status: registerResponse.status,
          bytes: total,
          product: productId,
          path: 'register',
          blobUrl: blob.url,
          responseSnippet: typeof registerData?.error === 'string' ? registerData.error.slice(0, 180) : undefined,
        }
      );
    }
    emit('error', 0);
    if (registerResponse.status === 400) throw new UploadValidationError(isContentRejection(registerData?.code) && options.invalidFileMessage ? options.invalidFileMessage : registerData?.error || failureMessage);
    throw new Error(registerData?.error || failureMessage);
  }

  emit('done', total);
  return { ...registerData.upload, ...(options.requireProof ? { proof: intent.proof } : {}) } as RegisteredUpload;
}
